import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Leaf, Calendar, Factory, Warehouse, Truck, Ship, Award, CheckCircle,
  Globe, MapPin, Thermometer, Droplets, Package, Shield, Clock,
  ChevronDown, ChevronUp, ExternalLink, QrCode
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicTrace = () => {
  const { batchId } = useParams();
  const [dossier, setDossier] = useState(null);
  const [qaRecord, setQaRecord] = useState(null);
  const [exportPack, setExportPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    farm: true,
    harvest: true,
    processing: false,
    storage: false,
    transport: false,
    qa: false,
    documents: false
  });

  useEffect(() => {
    fetchTraceData();
  }, [batchId]);

  const fetchTraceData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/trace/${batchId}`);
      if (!response.ok) {
        throw new Error('Shipment not found');
      }
      const data = await response.json();
      setDossier(data.dossier);
      setQaRecord(data.qa_record);
      setExportPack(data.export_pack);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading traceability data...</p>
          <p className="text-gray-400 text-sm mt-2">Batch: {batchId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 via-white to-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-700 mb-2">Shipment Not Found</h2>
            <p className="text-gray-500 mb-6">
              We couldn't find traceability data for batch ID: <br />
              <code className="bg-gray-100 px-2 py-1 rounded text-sm">{batchId}</code>
            </p>
            <Link to="/">
              <Button>Return to Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getQAProgress = () => {
    if (!qaRecord?.gates) return 0;
    const passed = qaRecord.gates.filter(g => g.overall_status === 'Passed').length;
    return Math.round((passed / qaRecord.gates.length) * 100);
  };

  const getDocProgress = () => {
    if (!exportPack?.documents) return 0;
    const required = exportPack.documents.filter(d => d.is_required);
    const completed = required.filter(d => d.status === 'Uploaded' || d.status === 'Verified');
    return Math.round((completed.length / required.length) * 100);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#263776] to-[#1a2a5c] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-green-300 text-sm mb-2">
            <Shield className="w-4 h-4" />
            <span>Verified Traceability Record</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">{dossier?.product_name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <code className="bg-white/20 px-3 py-1 rounded-full">{dossier?.batch_id}</code>
            <Badge className={`${
              dossier?.status === 'Shipped' ? 'bg-purple-500' :
              dossier?.status === 'Delivered' ? 'bg-green-500' :
              'bg-blue-500'
            }`}>
              {dossier?.status}
            </Badge>
            <span className="flex items-center gap-1">
              <Globe className="w-4 h-4" />
              {dossier?.origin_country} → {dossier?.destination_country}
            </span>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="max-w-4xl mx-auto px-4 -mt-4">
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{getQAProgress()}%</div>
              <div className="text-xs text-gray-500">QA Verified</div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{dossier?.certifications?.length || 0}</div>
              <div className="text-xs text-gray-500">Certifications</div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-lg">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-600">{getDocProgress()}%</div>
              <div className="text-xs text-gray-500">Docs Ready</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Journey Timeline */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-xl font-bold text-[#263776] mb-6 flex items-center gap-2">
          <Leaf className="w-5 h-5 text-green-600" />
          Farm to Fork Journey
        </h2>

        <div className="space-y-4">
          {/* Farm Source */}
          {dossier?.farm_details && (
            <Card className="border-l-4 border-l-green-500 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors py-4"
                onClick={() => toggleSection('farm')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Leaf className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Farm Source</CardTitle>
                      <p className="text-sm text-gray-500">{dossier.farm_details.farm_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {expandedSections.farm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
              </CardHeader>
              {expandedSections.farm && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Farmer</span>
                      <p className="font-medium">{dossier.farm_details.farmer_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Location</span>
                      <p className="font-medium">{dossier.farm_details.location}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Region</span>
                      <p className="font-medium">{dossier.farm_details.state_region}, {dossier.farm_details.country}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Cultivation</span>
                      <p className="font-medium">{dossier.farm_details.cultivation_method}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Soil Type</span>
                      <p className="font-medium">{dossier.farm_details.soil_type || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Irrigation</span>
                      <p className="font-medium">{dossier.farm_details.irrigation_method || 'N/A'}</p>
                    </div>
                    {dossier.farm_details.gps_coordinates && (
                      <div className="col-span-2 md:col-span-3">
                        <span className="text-gray-500">GPS Coordinates</span>
                        <p className="font-medium flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-green-600" />
                          {dossier.farm_details.gps_coordinates}
                        </p>
                      </div>
                    )}
                    {dossier.farm_details.images && dossier.farm_details.images.length > 0 && (
                      <div className="col-span-2 md:col-span-3">
                        <span className="text-gray-500 block mb-2">Farm Images</span>
                        <div className="flex gap-2 overflow-x-auto">
                          {dossier.farm_details.images.map((img, idx) => (
                            <img 
                              key={idx} 
                              src={img} 
                              alt={`Farm ${idx + 1}`} 
                              className="w-24 h-24 object-cover rounded-lg"
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Harvest */}
          {dossier?.harvest_details && (
            <Card className="border-l-4 border-l-amber-500 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors py-4"
                onClick={() => toggleSection('harvest')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Harvest</CardTitle>
                      <p className="text-sm text-gray-500">{dossier.harvest_details.harvest_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {expandedSections.harvest ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
              </CardHeader>
              {expandedSections.harvest && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Quantity</span>
                      <p className="font-medium">{dossier.harvest_details.quantity_kg} kg</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Quality Grade</span>
                      <p className="font-medium">{dossier.harvest_details.quality_grade}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Method</span>
                      <p className="font-medium">{dossier.harvest_details.harvest_method || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Weather</span>
                      <p className="font-medium">{dossier.harvest_details.weather_conditions || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Processing */}
          {dossier?.processing_details?.facility_name && (
            <Card className="border-l-4 border-l-blue-500 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors py-4"
                onClick={() => toggleSection('processing')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Factory className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Processing</CardTitle>
                      <p className="text-sm text-gray-500">{dossier.processing_details.facility_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {expandedSections.processing ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
              </CardHeader>
              {expandedSections.processing && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Location</span>
                      <p className="font-medium">{dossier.processing_details.facility_location}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Date</span>
                      <p className="font-medium">{dossier.processing_details.processing_date}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Method</span>
                      <p className="font-medium">{dossier.processing_details.processing_method}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Batch Input</span>
                      <p className="font-medium">{dossier.processing_details.batch_size_kg} kg</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Output</span>
                      <p className="font-medium">{dossier.processing_details.output_quantity_kg} kg</p>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Storage */}
          {dossier?.storage_details?.warehouse_name && (
            <Card className="border-l-4 border-l-purple-500 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors py-4"
                onClick={() => toggleSection('storage')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Warehouse className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Storage</CardTitle>
                      <p className="text-sm text-gray-500">{dossier.storage_details.warehouse_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {expandedSections.storage ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
              </CardHeader>
              {expandedSections.storage && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Location</span>
                      <p className="font-medium">{dossier.storage_details.warehouse_location}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Date</span>
                      <p className="font-medium">{dossier.storage_details.storage_date}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Duration</span>
                      <p className="font-medium">{dossier.storage_details.duration_days} days</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Thermometer className="w-4 h-4 text-blue-500" />
                      <div>
                        <span className="text-gray-500">Temperature</span>
                        <p className="font-medium">{dossier.storage_details.temperature || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Droplets className="w-4 h-4 text-blue-500" />
                      <div>
                        <span className="text-gray-500">Humidity</span>
                        <p className="font-medium">{dossier.storage_details.humidity || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )}

          {/* Transport */}
          {dossier?.transport_details?.transport_mode && (
            <Card className="border-l-4 border-l-orange-500 overflow-hidden">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors py-4"
                onClick={() => toggleSection('transport')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      {dossier.transport_details.transport_mode === 'Sea' ? 
                        <Ship className="w-5 h-5 text-orange-600" /> : 
                        <Truck className="w-5 h-5 text-orange-600" />
                      }
                    </div>
                    <div>
                      <CardTitle className="text-base">Transport ({dossier.transport_details.transport_mode})</CardTitle>
                      <p className="text-sm text-gray-500">
                        {dossier.transport_details.origin} → {dossier.transport_details.destination}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {dossier.status === 'Shipped' && <Clock className="w-5 h-5 text-amber-500" />}
                    {dossier.status === 'Delivered' && <CheckCircle className="w-5 h-5 text-green-500" />}
                    {expandedSections.transport ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>
              </CardHeader>
              {expandedSections.transport && (
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Vehicle</span>
                      <p className="font-medium">{dossier.transport_details.vehicle_type}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Departure</span>
                      <p className="font-medium">{dossier.transport_details.departure_date}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">ETA / Arrival</span>
                      <p className="font-medium">{dossier.transport_details.arrival_date}</p>
                    </div>
                    {dossier.transport_details.container_number && (
                      <div>
                        <span className="text-gray-500">Container</span>
                        <p className="font-medium font-mono">{dossier.transport_details.container_number}</p>
                      </div>
                    )}
                    {dossier.transport_details.cold_chain && (
                      <div className="flex items-center gap-2">
                        <Thermometer className="w-4 h-4 text-blue-500" />
                        <span className="font-medium text-blue-600">Cold Chain Maintained</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </div>

        {/* QA Summary */}
        {qaRecord && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-[#263776] mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              Quality Assurance
            </h2>
            <Card>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleSection('qa')}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">6-Stage QA Gates</CardTitle>
                    <p className="text-sm text-gray-500">{getQAProgress()}% Complete</p>
                  </div>
                  {expandedSections.qa ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
                <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-green-500"
                    style={{ width: `${getQAProgress()}%` }}
                  ></div>
                </div>
              </CardHeader>
              {expandedSections.qa && (
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {qaRecord.gates?.map((gate) => (
                      <div 
                        key={gate.gate_number}
                        className={`p-3 rounded-lg border ${
                          gate.overall_status === 'Passed' ? 'bg-green-50 border-green-200' :
                          gate.overall_status === 'Failed' ? 'bg-red-50 border-red-200' :
                          gate.overall_status === 'In Progress' ? 'bg-amber-50 border-amber-200' :
                          'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            gate.overall_status === 'Passed' ? 'bg-green-500 text-white' :
                            gate.overall_status === 'Failed' ? 'bg-red-500 text-white' :
                            gate.overall_status === 'In Progress' ? 'bg-amber-500 text-white' :
                            'bg-gray-300 text-gray-600'
                          }`}>
                            {gate.gate_number}
                          </div>
                          <div className="text-sm font-medium truncate">{gate.gate_name}</div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {gate.overall_status}
                          {gate.inspection_date && ` • ${gate.inspection_date}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        )}

        {/* Certifications */}
        {dossier?.certifications && dossier.certifications.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-[#263776] mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Certifications
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {dossier.certifications.map((cert, idx) => (
                <Card key={idx} className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-[#263776]">{cert.certification_name}</h4>
                        <p className="text-sm text-gray-600">{cert.certifying_body}</p>
                        {cert.certificate_number && (
                          <p className="text-xs font-mono text-gray-500 mt-1">{cert.certificate_number}</p>
                        )}
                      </div>
                      <Badge className={cert.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {cert.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Valid: {cert.valid_from} to {cert.valid_until}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Shield className="w-4 h-4 text-green-600" />
            Verified by SPANXPORT EximStack
          </div>
          <p className="text-xs text-gray-400">
            This traceability record is generated by SPANXPORT's supply chain management system.
            <br />
            For inquiries, contact us at{' '}
            <a href="mailto:info@spanxport.com" className="text-[#F47D20] hover:underline">
              info@spanxport.com
            </a>
          </p>
          <Link to="/" className="mt-4 inline-block">
            <Button variant="outline" size="sm">
              <ExternalLink className="w-4 h-4 mr-2" />
              Visit SPANXPORT
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PublicTrace;
